import React, { Fragment, useState, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { querySelectors, errorSelectors, mutateAsync } from 'redux-query';
import { FormikProps, WithFormikConfig, withFormik } from 'formik';
import * as yup from 'yup';
import cn from 'classnames';
import {
  Button,
  Classes,
  HeaderText,
  Icon,
  LinkButton,
  Popover,
  TextInput2,
  Text,
} from '@el8/vital';
import { Box, PasswordInput } from 'components';
import { login as loginAction, resetPasswordQuery, PatientQueryKeys } from 'modules/patient';
import {
  Dispatch,
  PatientLoginInfo,
  RQLoginResponse,
  State,
  FailureResponse,
  RQGenericResponse,
} from 'types';

import styles from './LoginPopover.less';

type OwnProps = {
  forceChartSelection?: boolean;
  practiceId: number;
  children: React.ReactNode;
};

type StateProps = {
  isLoggingIn: boolean;
  isSendingInstructions: boolean;
  loginError: FailureResponse;
};

type DispatchProps = {
  login: (credentials: PatientLoginInfo) => Promise<RQLoginResponse>;
  loginExplicit: (credentials: PatientLoginInfo) => Promise<RQLoginResponse>;
  resetPassword: (email: string) => Promise<RQGenericResponse>;
};

type FormValues = {
  email: string;
  password: string;
};

type Props = OwnProps & StateProps & DispatchProps & FormikProps<FormValues>;

const mapStateToProps = (state: State): StateProps => ({
  isLoggingIn: querySelectors.isPending(state.queries, {
    queryKey: PatientQueryKeys.PatientLogin,
    url: '',
  }),
  isSendingInstructions: querySelectors.isPending(state.queries, {
    queryKey: PatientQueryKeys.PatientPasswordReset,
    url: '',
  }),
  loginError: errorSelectors.responseBody(state.errors, {
    queryKey: PatientQueryKeys.PatientLogin,
    url: '',
  }),
});

const mapDispatchToProps = (dispatch: Dispatch, ownProps: OwnProps): DispatchProps => ({
  login(loginInfo: PatientLoginInfo): Promise<RQLoginResponse> {
    return dispatch(loginAction(loginInfo, ownProps.practiceId));
  },
  loginExplicit(loginInfo: PatientLoginInfo): Promise<RQLoginResponse> {
    return dispatch(loginAction(loginInfo, ownProps.practiceId, { explicitChartSelection: true }));
  },
  resetPassword(email): Promise<RQGenericResponse> {
    return dispatch(mutateAsync(resetPasswordQuery(email)));
  },
});

const formikConfig: WithFormikConfig<{}, FormValues> = {
  mapPropsToValues: () => ({
    email: '',
    password: '',
  }),
  validationSchema: yup.object().shape({
    email: yup.string().email().required(),
    password: yup.string().required(),
  }),
  // Because it have to be there
  handleSubmit: () => {},
};

const LoginPopover: React.FC<Props> = ({
  errors,
  forceChartSelection = false,
  handleChange,
  handleBlur,
  isLoggingIn,
  isSendingInstructions,
  isValid,
  login,
  loginError,
  loginExplicit,
  resetForm,
  resetPassword,
  children,
  touched,
  values,
}) => {
  const firstInputRef = useRef<HTMLInputElement>(null);
  const [isForgotPasswordFlow, setForgotPasswordFlow] = useState(false);
  const [wasInstructionsSent, setWasInstructionsSent] = useState(false);
  const [shouldShowLoginError, setShouldShowLoginError] = useState(false);

  const handleSendInstructions = (event: React.FormEvent): void => {
    event.preventDefault();
    resetPassword(values.email).then((): void => setWasInstructionsSent(true));
  };

  const handleBackToLoginClick = (): void => {
    setForgotPasswordFlow(false);
    setWasInstructionsSent(false);
  };

  const handleLogin = (event: React.FormEvent): void => {
    event.preventDefault();
    if (forceChartSelection) {
      loginExplicit(values);
    } else {
      login(values);
    }
    setShouldShowLoginError(true);
  };

  const handlePopoverDidOpen = (): void => {
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  };

  const handlePopoverClose = (): void => {
    setForgotPasswordFlow(false);
    setWasInstructionsSent(false);
    setShouldShowLoginError(false);
    resetForm({ values: { email: '', password: '' } });
  };

  return (
    <Popover
      popoverClassName={styles.login}
      popoverDidOpen={handlePopoverDidOpen}
      onClose={handlePopoverClose}
    >
      {children}
      {isForgotPasswordFlow && !wasInstructionsSent && (
        <form onSubmit={handleSendInstructions}>
          <Box align="center" className={styles.header} justify="space-between">
            <HeaderText tag="h1" legacyVariant="legacyHeaderText" sizing="heading-sm">
              Reset your password
            </HeaderText>
            <Icon className={cn(styles.close, Classes.POPOVER_DISMISS)} icon="cross" />
          </Box>
          <TextInput2
            className={styles.field}
            error={touched.email && errors.email}
            name="email"
            label="Email"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.email}
          />
          <Button
            className={styles.cta}
            disabled={Boolean(errors.email)}
            loading={isSendingInstructions}
            kind="primary"
            text="Send me instructions"
            type="submit"
          />
        </form>
      )}
      {isForgotPasswordFlow && wasInstructionsSent && (
        <Fragment>
          <Box align="center" className={styles.header} justify="space-between">
            <HeaderText tag="h1" legacyVariant="legacyHeaderText" sizing="heading-sm">
              Instructions sent!
            </HeaderText>
            <Icon className={cn(styles.close, Classes.POPOVER_DISMISS)} icon="cross" />
          </Box>
          <Text tag="p">
            <span>Instructions for resetting your password have been sent to </span>
            <Text weight="bold">{values.email}</Text>
          </Text>
          <Text tag="div">
            {`You will receive the email within 5 minutes. If you don't see it, try checking your spam folder.`}
          </Text>
          <Button
            className={styles.cta}
            kind="primary"
            onClick={handleBackToLoginClick}
            text="Back to login"
          />
        </Fragment>
      )}
      {!isForgotPasswordFlow && (
        <form onSubmit={handleLogin}>
          <Box align="center" className={styles.header} justify="space-between">
            <HeaderText tag="h1" legacyVariant="legacyHeaderText" sizing="heading-sm">
              Log into Passport
            </HeaderText>
            <Icon className={cn(styles.close, Classes.POPOVER_DISMISS)} icon="cross" />
          </Box>
          <TextInput2
            className={styles.field}
            error={(touched.email && errors.email) || (loginError && shouldShowLoginError)}
            inputRef={firstInputRef}
            name="email"
            label="Email"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.email}
          />
          <PasswordInput
            style={{ width: '100%' }}
            error={
              (touched.password && errors.password) ||
              (loginError && shouldShowLoginError && loginError.message)
            }
            name="password"
            label="Password"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.password}
          />
          <LinkButton
            className={styles.forgot}
            text="Forgot"
            onClick={(): void => setForgotPasswordFlow(true)}
          />
          <Button
            className={styles.cta}
            disabled={!isValid}
            loading={isLoggingIn}
            kind="primary"
            text="Log in"
            type="submit"
          />
        </form>
      )}
    </Popover>
  );
};

export default compose<React.FC<OwnProps>>(
  connect(mapStateToProps, mapDispatchToProps),
  withFormik(formikConfig),
)(LoginPopover);
